<script setup>
import { onBeforeUnmount, ref, watch } from 'vue';

const props = defineProps({
  documentViewer: {
    type: Object,
  },
  variant: {
    type: String,
    default: 'dark',
    validator: value => ['dark', 'light'].includes(value),
  },
});

const current_page = ref(1);
const page_input_value = ref(1);
const total_page_count = ref(0);

function updatePageState() {
  if (!props.documentViewer)
    return;
  current_page.value = props.documentViewer.getCurrentPage();
  page_input_value.value = current_page.value;
  total_page_count.value = props.documentViewer.getPageCount();
}

function onPageInputChange() {
  const page_number = Number.parseInt(page_input_value.value);
  if (Number.isNaN(page_number) || page_number < 1 || page_number > total_page_count.value) {
    page_input_value.value = current_page.value;
    return;
  }
  props.documentViewer.setCurrentPage(page_number);
}

watch(() => props.documentViewer, (new_instance, old_instance) => {
  old_instance?.removeEventListener('pageNumberUpdated', updatePageState);
  old_instance?.removeEventListener('documentLoaded', updatePageState);
  new_instance?.addEventListener('pageNumberUpdated', updatePageState);
  new_instance?.addEventListener('documentLoaded', updatePageState);
  updatePageState();
}, { immediate: true });

onBeforeUnmount(() => {
  props.documentViewer?.removeEventListener('pageNumberUpdated', updatePageState);
  props.documentViewer?.removeEventListener('documentLoaded', updatePageState);
});
</script>

<template>
  <template v-if="documentViewer && total_page_count">
    <slot name="before" />
    <div
      class="flex items-center gap-2 text-sm"
      :class="variant === 'light' ? 'h-9 px-2 bg-white border rounded-md drop-shadow-md text-gray-700' : 'text-white'"
    >
      <input
        v-model="page_input_value"
        type="number"
        min="1"
        :max="total_page_count"
        class="w-12 px-2 text-center focus:outline-none focus:border-primary-500 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        :class="variant === 'light' ? 'h-7 rounded-md border border-gray-300' : 'h-8 rounded-lg bg-gray-800 text-white border border-gray-600'"
        @keydown.stop
        @keydown.enter="$event.target.blur()"
        @blur="onPageInputChange"
      >
      <span>/ {{ total_page_count }}</span>
    </div>
  </template>
</template>
